import type { BulkEmailCompletePayload, BulkEmailProgressPayload } from '@fluenceur/types';
import type Pusher from 'pusher-js';
import type { Channel } from 'pusher-js';
import { createPusher, isPusherConfigured, PusherConfigurationError } from '@/libs/pusher-client';
import { pusherService } from './pusher-service';

const DEBUG = import.meta.env.DEV;

const SUBSCRIPTION_TIMEOUT_MS = 10000;
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 1000;

const log = DEBUG ? console.log.bind(console, '[BulkEmailPusher]') : () => {};
const error = DEBUG ? console.error.bind(console, '[BulkEmailPusher]') : () => {};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

interface BulkEmailCallbacks {
  onProgress: (data: BulkEmailProgressPayload) => void;
  onComplete: (data: BulkEmailCompletePayload) => void;
}

/**
 * Imperative Pusher subscription service for bulk email progress.
 * Follows the same pattern as BulkInvitePusherService.
 */
class BulkEmailPusherService {
  private pusher: Pusher | null = null;
  private channel: Channel | null = null;
  private currentChannelName: string | null = null;
  private callbacks: BulkEmailCallbacks | null = null;

  private initPusher(): Pusher {
    if (!this.pusher) {
      log('Initializing Pusher instance');
      this.pusher = createPusher({
        forceTLS: true,
        enabledTransports: ['ws', 'wss'],
        channelAuthorization: {
          endpoint: '/pusher/auth',
          transport: 'ajax',
          params: {},
          headers: {},
          customHandler: async (params, callback) => {
            try {
              const auth = await pusherService.authenticate(params.socketId, params.channelName);
              callback(null, auth);
            } catch (err) {
              error('Authentication failed:', err);
              callback(err as Error, null);
            }
          },
        },
      });
    }
    return this.pusher;
  }

  async subscribe(brandId: string, callbacks: BulkEmailCallbacks): Promise<void> {
    if (!isPusherConfigured) {
      throw new PusherConfigurationError();
    }

    const channelName = `private-brand-${brandId}`;

    if (this.channel && this.currentChannelName === channelName) {
      log('Already subscribed to channel, updating callbacks');
      this.callbacks = callbacks;
      return;
    }

    if (this.channel && this.currentChannelName !== channelName) {
      this.unsubscribe();
    }

    this.callbacks = callbacks;
    this.currentChannelName = channelName;

    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
      try {
        await this.attemptSubscription(channelName);
        return;
      } catch (err) {
        lastError = err instanceof Error ? err : new Error('Unknown subscription error');
        error(`Subscription attempt ${attempt}/${MAX_RETRY_ATTEMPTS} failed:`, lastError.message);

        if (attempt < MAX_RETRY_ATTEMPTS) {
          const delayMs = RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1);
          log(`Retrying in ${delayMs}ms...`);
          await delay(delayMs);
        }
      }
    }

    throw lastError || new Error('Pusher subscription failed after all retries');
  }

  private attemptSubscription(channelName: string): Promise<void> {
    const pusher = this.initPusher();

    return new Promise((resolve, reject) => {
      log('Subscribing to channel:', channelName);
      this.channel = pusher.subscribe(channelName);

      const timeout = setTimeout(() => {
        error('Subscription timeout');
        reject(new Error('Pusher subscription timeout'));
      }, SUBSCRIPTION_TIMEOUT_MS);

      this.channel.bind('pusher:subscription_succeeded', () => {
        clearTimeout(timeout);
        log('Successfully subscribed to channel:', channelName);

        this.channel!.bind('bulk-email-progress', (data: BulkEmailProgressPayload) => {
          log('Received progress event:', data);
          this.callbacks?.onProgress(data);
        });

        this.channel!.bind('bulk-email-complete', (data: BulkEmailCompletePayload) => {
          log('Received complete event:', data);
          this.callbacks?.onComplete(data);
        });

        resolve();
      });

      this.channel.bind('pusher:subscription_error', (err: Error) => {
        clearTimeout(timeout);
        error('Subscription error:', err);
        reject(err);
      });
    });
  }

  unsubscribe(): void {
    if (this.channel && this.currentChannelName) {
      log('Unsubscribing from channel:', this.currentChannelName);
      this.channel.unbind_all();
      this.pusher?.unsubscribe(this.currentChannelName);
      this.channel = null;
      this.currentChannelName = null;
      this.callbacks = null;
    }
  }

  disconnect(): void {
    this.unsubscribe();
    if (this.pusher) {
      log('Disconnecting Pusher');
      this.pusher.disconnect();
      this.pusher = null;
    }
  }
}

export const bulkEmailPusherService = new BulkEmailPusherService();
